import {
  type JSX,
  type Ref,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { Tour } from "./Tour";
import { type TourDataProps, tourDataSchema } from "./Tour.schema";
import type { TourCloseReason, TourStep } from "./Tour.types";
import { TOUR_STORAGE_PREFIX, TOUR_TAG_NAME } from "./constants";

export type RWCTourStep = Omit<TourStep, "content"> & {
  content: string;
};

export interface TourHandle {
  setSteps: (steps: RWCTourStep[]) => void;
  start: (options?: { force?: boolean }) => void;
  close: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TOUR_TAG_NAME]: {
      props: TourDataProps;
      handle: TourHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-tour": HTMLElement & TourHandle;
  }
}

function wasSeen(key: string): boolean {
  try {
    return localStorage.getItem(`${TOUR_STORAGE_PREFIX}${key}`) === "1";
  } catch {
    return false;
  }
}

function markSeen(key: string): void {
  try {
    localStorage.setItem(`${TOUR_STORAGE_PREFIX}${key}`, "1");
  } catch {
    // A tour that cannot be remembered is still a tour worth showing
  }
}

export function RWCTour({
  ref,
  storageKey,
  autoStart,
}: TourDataProps & { ref?: Ref<TourHandle> }): JSX.Element {
  const host = useIslandHost();

  const [steps, setSteps] = useState<TourStep[]>([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // The steps come from the page script, so the first ones to arrive are
  // what starts an automatic tour, once.
  const started = useRef(false);

  function emit(type: string, detail: Record<string, unknown> = {}) {
    host?.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
  }

  useImperativeHandle(
    ref,
    () => {
      const start = (options?: { force?: boolean }) => {
        if (!options?.force && storageKey && wasSeen(storageKey)) {
          return;
        }

        setStep(0);
        setOpen(true);
      };

      return {
        setSteps: (next) => {
          setSteps(next);

          if (autoStart && !started.current && next.length > 0) {
            started.current = true;
            start();
          }
        },
        start,
        close: () => setOpen(false),
      };
    },
    [storageKey, autoStart],
  );

  function handleStepChange(next: number) {
    setStep(next);

    emit("tour:step", { step: next, total: steps.length });
  }

  function handleClose(reason: TourCloseReason) {
    setOpen(false);

    if (storageKey) {
      markSeen(storageKey);
    }

    emit(reason === "complete" ? "tour:complete" : "tour:skip", { step });
  }

  return (
    <Tour
      steps={steps}
      open={open}
      step={step}
      onStepChange={handleStepChange}
      onClose={handleClose}
    />
  );
}

componentManager.register(
  rwc(TOUR_TAG_NAME, {
    component: RWCTour,
    dataSchema: tourDataSchema,
  }),
);
